import { useState } from "react";
import { toast } from "react-toastify";
import { markNotificationRead, reviewEwasteRequest } from "../../services/notification-service";

function convertUtcToLocal(utcString) {
    if (!utcString) return new Date();

    const safeUtc =
        utcString.includes("T") && !utcString.endsWith("Z") ? `${utcString}Z` : utcString;

    return new Date(safeUtc);
}

function formatTimestamp(iso) {
    const d = convertUtcToLocal(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const datePart = d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
    const timePart = d
        .toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })
        .toLowerCase();
    return `${datePart}, ${timePart}`;
}

function NotificationAccordion({ items }) {
    const [openId, setOpenId] = useState(null);
    const [openedIds, setOpenedIds] = useState({});
    const [readIds, setReadIds] = useState({});
    const [markingReadId, setMarkingReadId] = useState(null);
    const [reasonMap, setReasonMap] = useState({});
    const [reasonErrorMap, setReasonErrorMap] = useState({});
    const [processingId, setProcessingId] = useState(null);
    const [doneIds, setDoneIds] = useState({});

    const userData = JSON.parse(localStorage.getItem("userdata") || "{}");
    const userLoginId = userData?.id;

    const handleToggle = (n) => {
        const next = openId === n.id ? null : n.id;
        setOpenId(next);

        if (next && !openedIds[n.id]) {
            setOpenedIds((prev) => ({ ...prev, [n.id]: true }));
        }

        if (next && !n.isRead && !readIds[n.id] && markingReadId !== n.id) {
            setMarkingReadId(n.id);
            markNotificationRead(n.id, userLoginId)
                .then(() => {
                    setReadIds((prev) => ({ ...prev, [n.id]: true }));
                })
                .catch(() => { })
                .finally(() => {
                    setMarkingReadId(null);
                });
        }
    };

    const handleAction = async (n, status) => {
        const reason = reasonMap[n.id] ?? "";

        if (status === 2 && reason.trim() === "") {
            setReasonErrorMap((prev) => ({ ...prev, [n.id]: true }));
            toast.error("Reason is required to reject.");
            return;
        }
        setReasonErrorMap((prev) => ({ ...prev, [n.id]: false }));

        setProcessingId(n.id);
        try {
            const res = await reviewEwasteRequest(n.requestId, status, reason);
            toast.success(res?.message || (status === 1 ? "Approved successfully" : "Rejected successfully"));
            setDoneIds((prev) => ({ ...prev, [n.id]: status === 1 ? "Approve" : "Reject" }));
        } catch (err) {
            toast.error(err?.response?.data?.message || "Something went wrong");
        } finally {
            setProcessingId(null);
        }
    };

    return (
        <div className="flex flex-col gap-2">
            {items.map((n) => {
                const isOpen = openId === n.id;
                const isRead = n.isRead || !!readIds[n.id];
                const isDone = !!doneIds[n.id];
                const isActionRequired = !n.isActioned && !isDone;
                const isProcessing = processingId === n.id;

                return (
                    <div key={n.id} className="rounded-md bg-slate-50">
                        <button
                            type="button"
                            onClick={() => handleToggle(n)}
                            aria-expanded={isOpen}
                            className={`w-full rounded px-4 py-3 text-xs transition-colors flex items-center justify-between gap-3 ${!isRead ? "bg-orange-100 hover:bg-orange-200" : "bg-slate-100 hover:bg-slate-200"
                                }`}
                        >
                            <div className="flex flex-col items-start text-left">
                                <span className={`text-slate-700 ${!isRead ? "kr-font-medium" : "font-medium"}`}>
                                    {n.message}
                                </span>
                                <span className="text-slate-500">{formatTimestamp(n.createdOn)}</span>
                            </div>
                            <svg
                                width="12"
                                viewBox="0 0 24 24"
                                className={`shrink-0 fill-none stroke-slate-500 stroke-2 transition-transform ${isOpen ? "rotate-180" : ""
                                    }`}
                            >
                                <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>

                        {isOpen && (
                            <div className="p-4">
                                <div className="flex flex-col gap-4">
                                    <div>
                                        <label className="kr-font-medium mb-1 block text-xs text-slate-700">
                                            Details
                                        </label>
                                        <div className="kr-font-regular rounded border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-700">
                                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                                <div className="flex flex-col">
                                                    <span className="text-[11px] uppercase text-slate-400">Asset Type</span>
                                                    <span className="kr-font-regular text-xs uppercase text-slate-700">
                                                        {n.assetType || "—"}
                                                    </span>
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="text-[11px] uppercase text-slate-400">System Ids</span>
                                                    <span className="kr-font-regular text-xs uppercase text-slate-700">
                                                        {n.systemIds?.length > 0 ? n.systemIds.join(", ") : "—"}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {n.isRequired && (
                                        <>
                                            <div>
                                                <label className="kr-form-label mb-1 block">
                                                    Reason
                                                </label>
                                                {isActionRequired ? (
                                                    <textarea
                                                        rows={1}
                                                        placeholder="Enter reason"
                                                        value={reasonMap[n.id] ?? ""}
                                                        disabled={isDone}
                                                        className={`form-control kr-form-textbox placeholder-[#212529] w-full ${reasonErrorMap[n.id] ? "dataError" : ""
                                                            }`}
                                                        onChange={(e) => {
                                                            setReasonMap((prev) => ({ ...prev, [n.id]: e.target.value }));
                                                            if (reasonErrorMap[n.id]) {
                                                                setReasonErrorMap((prev) => ({ ...prev, [n.id]: false }));
                                                            }
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="kr-font-regular rounded border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-700">
                                                        {n.rejectionReason || "—"}
                                                    </div>
                                                )}
                                            </div>

                                            {(isActionRequired || isDone) && (
                                                <div className="flex items-center justify-end gap-2">
                                                    {isActionRequired && (
                                                        <>
                                                            <button
                                                                type="button"
                                                                className="btn btn-secondary flex items-center justify-center min-w-24"
                                                                disabled={isProcessing}
                                                                onClick={() => handleAction(n, 2)}
                                                            >
                                                                {isProcessing ? "..." : "Reject"}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-primary flex items-center justify-center min-w-24"
                                                                disabled={isProcessing}
                                                                onClick={() => handleAction(n, 1)}
                                                            >
                                                                {isProcessing ? "..." : "Approve"}
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export default NotificationAccordion;